"use client";

import { useRef } from "react";
import * as m from "motion/react-m";
import clsx from "clsx";
import { TextField, TextArea as AriaTextArea } from "react-aria-components";
import { useTranslations } from "next-intl";

import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import IconPaperclip2 from "@ui/central-icons/icons/IconPaperclip2";
import IconArrowUp from "@ui/central-icons/icons/IconArrowUp";
import type { FileAttachment } from "@ui/components/core/PromptIsland";

import { EASE_STANDARD, PILL_SHADOW } from "./types";

interface CreatePromptBarProps {
  promptText: string;
  onPromptChange: (value: string) => void;
  /** Re-enters edit mode on the latest result (attaches it as source chip).
   *  Bound to click too, so it fires even when the textarea kept focus after a generation. */
  onPromptFocus: () => void;
  /** True while a (canned) generation is in flight — swaps submit for a stop control. */
  isGenerating: boolean;
  /** A result has been revealed at least once — switches the placeholder copy. */
  hasResult: boolean;
  /** Submit allowed (some text or at least one attachment, and not generating). */
  canSubmit: boolean;
  onSubmit: () => void;
  onStop: () => void;
  onClose: () => void;
  /** The result being edited, surfaced as a source chip (the edition input). */
  editingSourceUrl?: string;
  /** Remove the source chip — resets create mode back to its initial empty state. */
  onRemoveEditingSource: () => void;
  files: FileAttachment[];
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (index: number) => void;
  /** Attachments are capped — keep the paperclip visible but disabled once full. */
  canAttach: boolean;
  /** Honour prefers-reduced-motion for chip fades. */
  reduceMotion: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}

const circleButton =
  "tw-flex tw-size-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-outline-none tw-transition-colors focus-visible:tw-outline-focus";
const attachmentPreviewClassName =
  "tw-group tw-relative tw-h-8 tw-w-12 tw-overflow-hidden tw-rounded-lg tw-transition-[box-shadow] hover:tw-ring-2 hover:tw-ring-black/10 focus-within:tw-ring-2 focus-within:tw-ring-black/10";
const attachmentRemoveClassName =
  "tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/35 tw-text-white tw-opacity-0 tw-outline-none tw-transition-opacity group-hover:tw-pointer-events-auto group-hover:tw-opacity-100 focus-visible:tw-opacity-100 focus-visible:tw-outline-focus";

export function CreatePromptBar({
  promptText,
  onPromptChange,
  onPromptFocus,
  isGenerating,
  hasResult,
  canSubmit,
  onSubmit,
  onStop,
  onClose,
  editingSourceUrl,
  onRemoveEditingSource,
  files,
  onAddFiles,
  onRemoveFile,
  canAttach,
  reduceMotion,
  textareaRef,
}: CreatePromptBarProps) {
  const t = useTranslations("ClientKeys.InteractiveImage");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fade = { duration: reduceMotion ? 0 : 0.15, ease: EASE_STANDARD };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!isGenerating && canSubmit) onSubmit();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      if (isGenerating) onStop();
      else onClose();
    }
  };

  const handleFilesPicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files;
    if (picked && picked.length > 0) onAddFiles(Array.from(picked));
    // Reset so picking the same file again still fires onChange.
    event.target.value = "";
  };

  const hasAttachments = !!editingSourceUrl || files.length > 0;
  const attachButton = (
    <button
      type='button'
      onClick={() => fileInputRef.current?.click()}
      disabled={!canAttach}
      aria-label={t("attach_reference")}
      className={clsx(
        circleButton,
        "tw-text-black/60 disabled:tw-opacity-40 enabled:hover:tw-bg-black/5 enabled:hover:tw-text-black",
      )}
    >
      <IconPaperclip2 className={clsx(iconStrokeFix, "tw-size-4")} />
    </button>
  );

  return (
    <div className='tw-flex tw-w-full tw-items-end tw-justify-center'>
      <div className='tw-relative tw-flex tw-w-full tw-min-w-0 tw-max-w-[31.25rem] tw-items-end'>
        <div
          className={clsx(
            "tw-flex tw-min-h-11 tw-w-full tw-min-w-0 tw-flex-col tw-rounded-[1.375rem] tw-bg-white tw-p-1.5",
            PILL_SHADOW,
          )}
        >
          {hasAttachments && (
            <div className='tw-mb-1 tw-flex tw-items-start tw-gap-1.5'>
              {attachButton}
              <ul className='tw-flex tw-min-w-0 tw-flex-1 tw-flex-wrap tw-gap-2'>
                {editingSourceUrl && (
                  <m.li
                    key='editing-source'
                    className={attachmentPreviewClassName}
                    initial={{ opacity: 0, scale: reduceMotion ? 1 : 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={fade}
                  >
                    {/* Edited result as source media; next/image is moot (URL is already sized). */}
                    <AttachmentChipBody
                      src={editingSourceUrl}
                      onRemove={onRemoveEditingSource}
                      removeLabel={t("remove_attachment")}
                    />
                  </m.li>
                )}
                {files.map((file, index) => (
                  <li key={file.previewUrl} className={attachmentPreviewClassName}>
                    {/* Local object-URL preview; next/image isn't useful for blob: sources. */}
                    <AttachmentChipBody
                      src={file.previewUrl}
                      onRemove={() => onRemoveFile(index)}
                      removeLabel={t("remove_attachment")}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={clsx("tw-flex tw-items-end", hasAttachments && "tw-pl-9")}>
            {!hasAttachments && attachButton}
            <input
              ref={fileInputRef}
              type='file'
              accept='image/*'
              multiple
              onChange={handleFilesPicked}
              className='tw-hidden'
            />

            <TextField
              aria-label={hasResult ? t("edit_image_placeholder") : t("describe_image_placeholder")}
              value={promptText}
              onChange={onPromptChange}
              className='tw-flex tw-min-w-0 tw-flex-1'
            >
              <AriaTextArea
                ref={textareaRef}
                onKeyDown={handleKeyDown}
                onFocus={onPromptFocus}
                // Also on click: textarea often keeps focus after a generation, so a click with
                // no new focus event must still re-enter edit mode on the fresh result.
                onClick={onPromptFocus}
                placeholder={hasResult ? t("edit_image_placeholder") : t("describe_image_placeholder")}
                rows={1}
                className='tw-min-h-8 tw-max-h-[7.5rem] tw-min-w-0 tw-flex-1 tw-resize-none tw-overflow-y-auto tw-bg-transparent tw-px-1 tw-py-1 tw-text-black tw-type-base md:tw-type-sm tw-outline-none placeholder:tw-text-black/40 [field-sizing:content]'
              />
            </TextField>

            <button
              type='button'
              onClick={isGenerating ? onStop : onSubmit}
              disabled={!isGenerating && !canSubmit}
              aria-label={isGenerating ? t("stop") : t("submit")}
              className={clsx(
                circleButton,
                "tw-bg-black tw-text-white tw-transition-opacity",
                !isGenerating && !canSubmit ? "tw-cursor-not-allowed tw-opacity-40" : "hover:tw-opacity-80",
              )}
            >
              {isGenerating ? (
                <span aria-hidden className='tw-size-2.5 tw-rounded-[0.1875rem] tw-bg-current' />
              ) : (
                <IconArrowUp className={clsx(iconStrokeFix, "tw-size-4")} />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Shared chip contents (preview image + hover/focus remove overlay) for both the edited-result
// source chip and the picked-file chips, which differ only in image source and remove handler.
function AttachmentChipBody({
  src,
  onRemove,
  removeLabel,
}: {
  src: string;
  onRemove: () => void;
  removeLabel: string;
}) {
  return (
    <>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={src} alt='' className='tw-size-full tw-object-cover' />
      <button type='button' onClick={onRemove} aria-label={removeLabel} className={attachmentRemoveClassName}>
        <AttachmentTrashIcon />
      </button>
    </>
  );
}

function AttachmentTrashIcon() {
  return (
    <svg width='14' height='14' viewBox='0 0 14 14' fill='none' aria-hidden>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M3.61154 2C4.12584 0.822984 5.29981 0 6.66729 0C8.03478 0 9.20874 0.822984 9.72304 2H12.6667C13.0349 2 13.3333 2.29848 13.3333 2.66667C13.3333 3.03486 13.0349 3.33333 12.6667 3.33333H11.957L11.4148 11.4664C11.3448 12.517 10.4722 13.3333 9.41926 13.3333H3.91407C2.86114 13.3333 1.98854 12.517 1.9185 11.4664L1.3763 3.33333H0.666667C0.298477 3.33333 0 3.03486 0 2.66667C0 2.29848 0.298477 2 0.666667 2H3.61154ZM5.17641 2C5.54283 1.59075 6.07534 1.33333 6.66729 1.33333C7.25924 1.33333 7.79175 1.59075 8.15817 2H5.17641ZM6 6C6 5.63181 5.70152 5.33333 5.33333 5.33333C4.96514 5.33333 4.66667 5.63181 4.66667 6V9.33333C4.66667 9.70152 4.96514 10 5.33333 10C5.70152 10 6 9.70152 6 9.33333V6ZM8 5.33333C8.36819 5.33333 8.66667 5.63181 8.66667 6V9.33333C8.66667 9.70152 8.36819 10 8 10C7.63181 10 7.33333 9.70152 7.33333 9.33333V6C7.33333 5.63181 7.63181 5.33333 8 5.33333Z'
        fill='currentColor'
      />
    </svg>
  );
}
